<script setup lang="ts">
import type { ReactiveSearch } from "~/classes/search/ReactiveSearch"

import agentSearchFolder from "~/assets/images/files/folder/agent_search@64.webp"

const props = defineProps({
  search: null,
  fractionalIndex: null,
  dragOver: { type: Boolean }
})
const { isSelectedIn } = useSelection()
const { isDragging } = useGestures()

const facets = computed(() => {
  const definition = props.search.definition()
  if (isDefined(definition)) {
    let facets = definition.facets
    // if we have a "contains" and a "starts-with" filter we remove the starts-with
    const hasContains = any(definition.facets, f => "contains" in f.facetType)
    const hasStartsWith = any(definition.facets, f => "startsWith" in f.facetType)
    if (hasContains && hasStartsWith)
      facets = definition.facets.filter(f => !("startsWith" in f.facetType))

    // if we have more than one near filter, we remove the image and video text filters
    // since doc-like text is the most general one
    const isNearFilter = (f: SearchFacet) => (
      "nearImageText" in f.facetType
      || "nearDocLikeText" in f.facetType
      || "nearVideoText" in f.facetType
    )
    const nearFilters = definition.facets.filter(isNearFilter)
    if (len(nearFilters) > 1)
      facets = facets.filter(f => !isNearFilter(f) || deepEqual(f, nearFilters[0]))

    return facets
  }
  else {
    return []
  }
})

const sorts = computed(() => {
  const definition = props.search.definition()
  if (isDefined(definition))
    return definition.sorts
  else
    return []
})
</script>

<template>
  <sidebar-entry-item
    :search-id="search.id"
    :fractional-index
  >
    <template #item>
      <div class="agent-search">
        <div v-if="search.description()" class="item">
          <div class="search-thumbnail">
            <img draggable="false" :src="agentSearchFolder">
          </div>
          <div class="description">
            {{ search.description() }}
          </div>
        </div>
        <div v-else class="item">
          <div class="search-thumbnail">
            <img draggable="false" :src="agentSearchFolder">
          </div>
          <search-path-facet
            v-for="facet, key in facets"
            :key
            :facet
          />
          <search-path-sort
            v-for="sort, key in sorts"
            :key
            :sort
          />
        </div>
      </div>
    </template>
    <template #contents>
      <sidebar-entry-search-list :search-id="search.id" />
    </template>
  </sidebar-entry-item>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.search-thumbnail
  @apply h-4.5 w-4.5 aspect-square flex-none
.item
  @apply flex flex-none items-center h-full gap-1.5
.description
  @apply text-ellipses-inline
</style>
